import React, { useCallback, useEffect } from 'react'
import { Handle, Position } from '@xyflow/react'
import Utils from '../Utils'
import Api from '../Api'
import { dragConnectTypes } from '../translate'
import CallIcon from '@mui/icons-material/Call'
import PersonIcon from '@mui/icons-material/Person'
import GroupIcon from '@mui/icons-material/Group'
import MenuIcon from '@mui/icons-material/Menu'

function CustomNode(prop) {
  const { id, data } = prop
  const toDragSingleType = (rawType) => {
    const key = (rawType || '').toLowerCase()
    if (key === 'sip') return 'sip'
    if (key === 'group' || key === 'short' || key === 'ivr') return 'group'
    return ''
  }

  const getLang = () => +localStorage.getItem('atsLang') || 1

  const formatTypeLabel = (raw) => {
    const key = (raw || '').toUpperCase()
    const groupFallback = ['', 'Group', 'Группа', 'Խումբ']
    return dragConnectTypes[key]?.[getLang()] || (key === 'GROUP' ? groupFallback[getLang()] : key || '')
  }

  const getTypeIcon = (typeKey) => {
    const key = (typeKey || '').toUpperCase()
    if (key === 'DID') return <CallIcon sx={{ fontSize: 20, opacity: 0.9 }} />
    if (key === 'SIP') return <PersonIcon sx={{ fontSize: 20, opacity: 0.9 }} />
    if (key === 'GROUP' || key === 'SHORT') return <GroupIcon sx={{ fontSize: 20, opacity: 0.9 }} />
    if (key === 'IVR') return <MenuIcon sx={{ fontSize: 20, opacity: 0.9 }} />
    return null
  }

  const getSubLabelFromItem = (item) =>
    (item?.notes ?? item?.name ?? item?.context ?? item?.title ?? '').toString().trim()

  // centers children under this node
  const rowX = (centerX, index, count, gap) => centerX + (index - (count - 1) / 2) * gap

  const handleClick = useCallback(
    async (label) => {
      if (!data?.setNodes || !label?.uid || !label?.type || !data?.label || data?.label === 'undefined') {
        return
      }
      const typeKey = String(label.type || '').toUpperCase()
      if (typeKey === 'DID' || typeKey === 'INCLUDED') return
      try {
        const apiType = toDragSingleType(label.type)
        if (!apiType) return
        const color = Utils.getRandomColor()
        const response = await Api.dragSingle(label.uid, apiType)

        if (!response?.data?.data?.length || !response?.data?.data[0]) return

        const rawItems = response.data.data || []
        const seen = new Set()
        const uniqueData = rawItems.filter((s) => {
          const k = `${s.id}_${s.buddy}`
          if (seen.has(k)) return false
          seen.add(k)
          return true
        })

        const gap = 180

        data.setNodes((prevNodes) => {
          const childCount = uniqueData.length
          const ivrNoAction = label?.noaction?.toString()
          const showNoActionShort =
            String(label?.type || '').toUpperCase() === 'IVR' && !!ivrNoAction && childCount > 0
          const shortNodeId = showNoActionShort ? `${ivrNoAction}_${data.lid}` : null

          if (showNoActionShort) {
            // Add IVR fallback short as intermediate node: IVR -> SHORT -> items.
            data.setEdges((prevEdges) => {
              const edgeId = `${shortNodeId}_from_${data.lid}`
              if (prevEdges.some((e) => e.id === edgeId)) return prevEdges
              return [
                ...prevEdges,
                {
                  id: edgeId,
                  source: data.lid,
                  sourceHandle: `${data.type}_source_${data.lid}` || 'source',
                  target: shortNodeId,
                  targetHandle: `GROUP_target_${shortNodeId}`,
                  type: 'straight',
                  animated: true,
                },
              ]
            })
          }

          const newNodes = uniqueData.map((s, i) => {
            const newId = `${s.id}_${s.buddy}`
            const childType = response.data.label ? response.data.label.toUpperCase() : 'SIP'

            // ✅ centered under parent
            const posX = rowX(label.position.x, i, childCount, gap)
            const posY = showNoActionShort ? label.position.y + 400 : label.position.y + 200

            // ✅ add straight edge (avoid duplicates)
            data.setEdges((prevEdges) => {
              const edgeId = `${s.id}_${data.lid}`
              if (prevEdges.some((e) => e.id === edgeId)) return prevEdges

              return [
                ...prevEdges,
                {
                  id: edgeId,
                  source: showNoActionShort ? shortNodeId : data.lid,
                  sourceHandle: showNoActionShort
                    ? `GROUP_source_${shortNodeId}`
                    : `${data.type}_source_${data.lid}` || 'source',
                  target: newId,
                  targetHandle: `${childType}_target_${newId}` || 'target',
                  type: 'straight',
                  animated: true,
                },
              ]
            })

            const sub = getSubLabelFromItem(s)

            return {
              id: newId,
              type: 'custom',
              position: { x: posX, y: posY },
              data: {
                color,
                label: `${s.buddy}`,
                subLabel: sub,
                type: childType,
                displayType: formatTypeLabel(childType),
                uid: s.buddy,
                lid: newId,
                child_type: childType,
                noaction: s?.noaction,
                position: { x: posX, y: posY },
                setNodes: data.setNodes,
                setEdges: data.setEdges,
              },
            }
          })

          if (showNoActionShort && !prevNodes.some((node) => node.id === shortNodeId)) {
            newNodes.unshift({
              id: shortNodeId,
              type: 'custom',
              position: { x: label.position.x, y: label.position.y + 200 },
              data: {
                color,
                label: ivrNoAction,
                subLabel: '',
                type: 'GROUP',
                displayType: formatTypeLabel('GROUP'),
                uid: ivrNoAction,
                lid: shortNodeId,
                child_type: 'GROUP',
                position: { x: label.position.x, y: label.position.y + 200 },
                setNodes: data.setNodes,
                setEdges: data.setEdges,
              },
            })
          }

          const filteredNodes = newNodes.filter((newNode) => !prevNodes.some((node) => node.id === newNode.id))
          return [...prevNodes, ...filteredNodes]
        })
      } catch (error) {
        console.error('Error in handleClick:', error)
      }
    },
    [data]
  )

  useEffect(() => {
    handleClick(data)
  }, [handleClick, data])

  return (
    <>
      <Handle position={Position.Top} type="target" id={`${data.type}_target_${id}`} />

      <div className="custom_node" style={data.color ? { borderColor: data.color } : {}}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', color: data.color || '#64748b' }}>
            {getTypeIcon(data.type)}
          </span>
          {!data.hideTypeLabel ? <b>{data.displayType || formatTypeLabel(data.type)}</b> : null}
        </div>

        {data.label && data.label !== 'undefined' ? <div>{data.label}</div> : null}

        {data.subLabel ? (
          <div style={{ marginTop: 4, fontSize: 12, color: '#64748b', lineHeight: 1.2 }}>
            {data.subLabel}
          </div>
        ) : null}
      </div>

      <Handle type="source" position={Position.Bottom} id={`${data.type}_source_${id}`} />
    </>
  )
}

export default CustomNode
